import React, { useEffect, useRef } from 'react';
import { useKeyPressEvent } from 'react-use';
import { Portal } from 'react-portal';
import { AnimatePresence } from 'framer-motion';
import { useScrollLock } from '~/shared/hooks';
import { Close, Content, Header, Main, Wrapper } from './styled';
import WhiteCloseIcon from '~/icons/close-white.svg';

export const Modal = ({ isOpen = false, onClose, ...rest }: any) => (
    <AnimatePresence initial mode="wait" onExitComplete={onClose}>
        {isOpen && <ModalComponent {...rest} />}
    </AnimatePresence>
);

export type ModalTypes = 'gallery' | 'search' | 'searchMobile';

type ModalProps = {
    handleClose: any;
    children: any;
    variant: ModalTypes;
    target?: any;
    showCloseIcon?: boolean;
    marginTop?: number;
};

const ModalComponent = ({
    handleClose,
    children,
    variant,
    target,
    showCloseIcon,
    marginTop,
    ...rest
}: ModalProps & { [key: string]: any }) => {
    const { lock, unlock } = useScrollLock();
    // const shouldReduceMotion = useReducedMotion();
    const wrapperRef = useRef(null);

    useKeyPressEvent('Escape', () => {
        if (handleClose) {
            handleClose();
        }
    });

    const handleWrapperClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
        if (wrapperRef.current === e.target) {
            if (handleClose) {
                handleClose();
            }
        }
    };

    // Lock scroll when modal is open
    useEffect(() => {
        lock();
        return () => unlock();
    }, [lock, unlock]);

    return (
        <Portal {...(target && { node: target })}>
            <Wrapper
                marginTop={marginTop}
                variant={variant}
                onClick={handleWrapperClick}
                ref={wrapperRef}
                {...rest}
            >
                <Main
                    variant={variant}
                    variants={variants}
                    animate="open"
                    exit="closed"
                    initial="closed"
                    transition={transition}
                >
                    {showCloseIcon && (
                        <Header>
                            <Close onClick={handleClose}>
                                <WhiteCloseIcon />
                            </Close>
                        </Header>
                    )}
                    <Content variant={variant}>{children}</Content>
                </Main>
            </Wrapper>
        </Portal>
    );
};

const variants: any = {
    open: {
        x: 0,
        y: 0,
    },
    closed: {
        x: 0,
        y: 0,
    },
};

const transition = {
    duration: 1000,
    type: 'spring',
    stiffness: 600,
    damping: 50,
};
